import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Coffee, MapPin, Phone, Clock, Star, Instagram } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002';
const API = `${BACKEND_URL}/api`;

const MenuPage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    fetchMenuData();
    checkOpenStatus();
  }, []);

  const fetchMenuData = async () => {
    try {
      const res = await fetch(`${API}/menu`);
      const data = await res.json();
      setCategories(data.categories || []);
      setProducts(data.products || []);
      if (data.categories?.length > 0) {
        setActiveCategory(data.categories[0].id);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const checkOpenStatus = () => {
    const now = new Date();
    const hours = now.getHours();
    setIsOpen(hours >= 8 || hours === 0);
  };

  const filteredProducts = activeCategory
    ? products.filter(p => p.category_id === activeCategory)
    : products;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0c0a09] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#d6b696] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a2e23]" data-testid="menu-page">
      {/* Header */}
      <header className="bg-[#2d4a3e] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <img
                src="https://customer-assets.emergentagent.com/job_premium-brew-36/artifacts/glj0sgxs_WhatsApp%20Image%202026-02-18%20at%2018.37.17.jpeg"
                alt="Polje Coffee Co"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="font-['Playfair_Display'] text-lg text-[#f5f0e1]">Polje Coffee Co</span>
            </a>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <span className={`text-xs ${isOpen ? 'text-green-500' : 'text-red-500'}`}>
                {isOpen ? 'Açık' : 'Kapalı'}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&q=80"
          alt="Polje Coffee Co"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a2e23] via-[#1a2e23]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#f5f0e1] mb-2">
              Menümüz
            </h1>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#d4a84b] text-[#d4a84b]" />
                ))}
                <span className="text-[#f5f0e1] ml-1">5.0</span>
              </div>
              <span className="text-[#b8c4b8]">•</span>
              <span className="text-[#b8c4b8]">Ayrancı, Ankara</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      {categories.length > 0 && (
        <div className="sticky top-[60px] z-40 bg-[#1a2e23] border-b border-[#3d5a4e]">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex overflow-x-auto scrollbar-hide gap-2 py-3 -mx-4 px-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${activeCategory === category.id
                    ? 'bg-[#d4a84b] text-[#1a2e23]'
                    : 'bg-[#2d4a3e] text-[#b8c4b8] hover:bg-[#3d5a4e]'
                    }`}
                  data-testid={`category-tab-${category.id}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Products */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {categories.length === 0 || products.length === 0 ? (
          <div className="text-center py-16">
            <Coffee className="w-16 h-16 text-[#3d5a4e] mx-auto mb-4" />
            <p className="text-[#b8c4b8] text-lg">Menü hazırlanıyor...</p>
            <p className="text-[#6b7b6b] text-sm mt-2">Yakında burada lezzetli ürünlerimizi göreceksiniz.</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <Coffee className="w-16 h-16 text-[#3d5a4e] mx-auto mb-4" />
            <p className="text-[#b8c4b8] text-lg">Bu kategoride ürün bulunmuyor.</p>
          </div>
        ) : (
          <motion.div
            key={activeCategory}
            className="grid gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-[#2d4a3e] rounded-lg overflow-hidden flex"
                data-testid={`product-card-${product.id}`}
              >

                {/* Content */}
                <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                  <div>
                    <h3 className="font-['Playfair_Display'] text-lg text-[#f5f0e1] truncate">
                      {product.name}
                    </h3>
                  </div>
                  <div className="mt-2">
                    <span className="text-[#d4a84b] font-semibold text-lg">
                      ₺{product.price.toFixed(2)}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Footer Info */}
      <div className="bg-[#2d4a3e] mt-8">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#d4a84b] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#f5f0e1] font-medium text-sm">Adres</p>
                <p className="text-[#b8c4b8] text-sm">
                  Ayrancı, Hoşdere Cd. No:116 D:C<br />
                  06540 Çankaya / Ankara
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-[#d4a84b] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#f5f0e1] font-medium text-sm">Telefon</p>
                <a href="tel:+905324846373" className="text-[#b8c4b8] text-sm hover:text-[#d4a84b] transition-colors">
                  0532 484 63 73
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Instagram className="w-5 h-5 text-[#d4a84b] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#f5f0e1] font-medium text-sm">Instagram</p>
                <a href="https://www.instagram.com/poljecoffeeco/" target="_blank" rel="noopener noreferrer" className="text-[#b8c4b8] text-sm hover:text-[#d4a84b] transition-colors">
                  @poljecoffeeco
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#d4a84b] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#f5f0e1] font-medium text-sm">Çalışma Saatleri</p>
                <p className="text-[#b8c4b8] text-sm">Her gün 08:00 - 00:00</p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-[#3d5a4e] text-center">
            <a href="/" className="text-[#b8c4b8] text-sm hover:text-[#d4a84b] transition-colors">
              ← Ana Sayfaya Dön
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuPage;
